/**
 * Created by Daniel on 2/18/2020.
 * Description:
 *
 * ------ maintenance history ------
 */

import { Injectable } from '@angular/core';
import { Action } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of, forkJoin } from 'rxjs';
import { switchMap, map, catchError, takeUntil } from 'rxjs/operators';
import { WorkflowTaskService } from '../../services/workflow-task.service';
import {
    WorkflowTaskActionTypes, CreateQuickTask, EditQuickTask, ChangeQuickTaskStatus,
    GetTaskByStatus, EditQuickTaskRemindDate, DeleteTask,
} from '../actions/workflow-task.actions';
import { GetAllTask } from '../actions/workflow-task.actions';
import { TaskItem, QuickTask, SubTask, Processor } from '../../models/workflow.model';

@Injectable()
export class WorkflowTaskEffects {

    constructor(
        private _actions$: Actions,
        private _service: WorkflowTaskService,
    ) { }

    changeTaskStatusEffect$: Observable<Action> = createEffect(() => this._actions$.pipe(
        ofType(WorkflowTaskActionTypes.CHANGE_QUICK_TASK_STATUS),
        switchMap((action: ChangeQuickTaskStatus
        ) => {
            const payload = action.payload;
            return this._service.changeQuickTaskStatus(payload).pipe(
                map(res => {
                    if (res) {
                        return ({
                            type: WorkflowTaskActionTypes.CHANGE_QUICK_TASK_STATUS_SUCCESS,
                            payload: res
                        });
                    }
                }),
                catchError(err => {
                    return of({
                        type: WorkflowTaskActionTypes.CHANGE_QUICK_TASK_STATUS_FAILED,
                        payload: err.message
                    });
                })
            );
        })
    ));

    createTaskEffect$: Observable<Action> = createEffect(() => this._actions$.pipe(
        ofType(WorkflowTaskActionTypes.CREATE_QUICK_TASK),
        switchMap((action: CreateQuickTask
        ) => {
            const payload = action.payload;
            return this._service.createQuickTask(payload).pipe(
                map(res => {
                    if (res) {
                        return ({
                            type: WorkflowTaskActionTypes.CREATE_QUICK_TASK_SUCCESS,
                            payload: res
                        });
                    }
                }),
                catchError(err => {
                    return of({
                        type: WorkflowTaskActionTypes.CREATE_QUICK_TASK_FAILED,
                        payload: err.message
                    });
                })
            );
        })
    ));

    deleteTaskEffect$: Observable<Action> = createEffect(() => this._actions$.pipe(
        ofType(WorkflowTaskActionTypes.DELETE_TASK),
        switchMap((action: DeleteTask
        ) => {
            const payload = action.payload;
            const task = payload.task;
            const type = payload.type;

            return this._service.deleteTask(task, type).pipe(
                map(res => {
                    return ({
                        type: WorkflowTaskActionTypes.DELETE_TASK_SUCCESS,
                        payload: task.subject
                    });
                }),
                catchError(err => {
                    return of({
                        type: WorkflowTaskActionTypes.DELETE_TASK_FAILED,
                        payload: err.message
                    });
                })
            );
        })
    ));

    editTaskEffect$: Observable<Action> = createEffect(() => this._actions$.pipe(
        ofType(WorkflowTaskActionTypes.EDIT_QUICK_TASK),
        switchMap((action: EditQuickTask
        ) => {
            const payload = action.payload.quickTask;
            const triggerBy = action.payload.triggerBy;
            return this._service.editQuickTask(payload).pipe(
                map(res => {
                    if (res) {
                        return ({
                            type: WorkflowTaskActionTypes.EDIT_QUICK_TASK_SUCCESS,
                            payload: triggerBy
                        });
                    }
                }),
                catchError(err => {
                    return of({
                        type: WorkflowTaskActionTypes.EDIT_QUICK_TASK_FAILED,
                        payload: err.message
                    });
                })
            );
        })
    ));

    editTaskReminderEffect$: Observable<Action> = createEffect(() => this._actions$.pipe(
        ofType(WorkflowTaskActionTypes.EDIT_QUICK_TASK_REMIND_DATE),
        switchMap((action: EditQuickTaskRemindDate
        ) => {
            const quickTask = action.payload.quickTask;
            const subTaskId = action.payload.subTaskId;
            const remindDate = action.payload.remindDate;
            const triggerBy = action.payload.triggerBy;
            const observables$ = [];
            const statusData = {
                subTaskId: subTaskId,
                remindDate: remindDate,
            };

            observables$.push(this._service.editQuickTask(quickTask));
            observables$.push(this._service.changeQuickTaskStatus(statusData));

            return forkJoin(observables$).pipe(
                map(response => {
                    return ({
                        type: WorkflowTaskActionTypes.EDIT_QUICK_TASK_REMIND_DATE_SUCCESS,
                        payload: triggerBy
                    });
                }),
                catchError(err => {
                    return of({
                        type: WorkflowTaskActionTypes.EDIT_QUICK_TASK_REMIND_DATE_FAILED,
                        payload: err.message
                    });
                })
            );
        })
    ));

    getAllTaskEffect$: Observable<Action> = createEffect(() => this._actions$.pipe(
        ofType(WorkflowTaskActionTypes.GET_ALL_TASK),
        switchMap((action: GetAllTask
        ) => {
            const payload = action.payload;
            return this._service.getAllTasks().pipe(
                map(res => {
                    if (res) {
                        const quickTaskList = [];
                        res['quick-task-definition-list'].forEach((_res: any) => {
                            const quickTask = QuickTask.parse(_res);
                            let taskItem = new TaskItem();
                            taskItem = TaskItem.parseQuickTask(quickTask);
                            quickTaskList.push(taskItem);
                        });

                        return ({
                            type: WorkflowTaskActionTypes.GET_ALL_TASK_SUCCESS,
                            payload: quickTaskList
                        });
                    }
                }),
                catchError(err => {
                    return of({
                        type: WorkflowTaskActionTypes.GET_ALL_TASK_FAILED,
                        payload: err.message
                    });
                })
            );
        })
    ));

    getTaskByStatusEffect$: Observable<Action> = createEffect(() => this._actions$.pipe(
        ofType(WorkflowTaskActionTypes.GET_TASK_BY_STATUS),
        switchMap((action: GetTaskByStatus
        ) => {
            const payload = action.payload;
            const processor = payload === null || payload === undefined ? 0 : payload.processor;
            const status = payload === null || payload === undefined ? 0 : payload.status;
            if (processor === Processor.CreatedByMe) {
                return this._service.getTaskByStatus(status).pipe(
                    map(res => {
                        if (res) {
                            const quickTaskList = [];
                            res['quick-task-definition-list'].forEach((_res: any) => {
                                const quickTask = QuickTask.parse(_res);
                                let taskItem = new TaskItem();
                                taskItem = TaskItem.parseQuickTask(quickTask);
                                quickTaskList.push(taskItem);
                            });

                            return ({
                                type: WorkflowTaskActionTypes.GET_TASK_BY_STATUS_SUCCESS,
                                payload: quickTaskList
                            });
                        }
                    }),
                    catchError(err => {
                        return of({
                            type: WorkflowTaskActionTypes.GET_TASK_BY_STATUS_FAILED,
                            payload: err.message
                        });
                    })
                );
            } else {
                return this._service.getTaskByProcessor(status).pipe(
                    map(res => {
                        if (res) {
                            const quickTaskList = [];
                            res['quick-task-list'].forEach((_res: any) => {
                                const subTask = SubTask.parse(_res);
                                let taskItem = new TaskItem();
                                taskItem = TaskItem.parseSubTask(subTask);
                                quickTaskList.push(taskItem);
                            });

                            return ({
                                type: WorkflowTaskActionTypes.GET_TASK_BY_STATUS_SUCCESS,
                                payload: quickTaskList
                            });
                        }
                    }),
                    catchError(err => {
                        return of({
                            type: WorkflowTaskActionTypes.GET_TASK_BY_STATUS_FAILED,
                            payload: err.message
                        });
                    })
                );
            }
        })
    ));
}
